import React from 'react';
import { Button, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import copy from 'copy-to-clipboard';
import { PATH } from 'constants/path';
import dayjs from 'utils/dayjs';
import useTimezone from 'hooks/useTimezone';
import { EAppointmentContactTypeDictKey } from 'types/common';
import useGetTodayAppointments from 'hooks/useGetTodayAppointments';
import { isKlarityMeetLink } from 'utils/videoLink';
import ChatIcon from './icons/ChatIcon';
import ClockIcon from './icons/ClockIcon';
import ClockIconColored from './icons/ClockIconColored';
import ExternalLinkIcon from './icons/ExternalLinkIcon';
import ArrowRightIcon from './icons/ArrowRightIcon';
import s from './s.module.less';

const TodayAppointments: React.FC = () => {
    const [appointments, loading] = useGetTodayAppointments();
    const navigate = useNavigate();
    const [, myTimezone] = useTimezone();

    const handleViewProfile = (clientId: number) => {
        window.open(`${PATH.DASHBOARD}/${PATH.PROSPECTS_DETAIL}/${clientId}`, '_blank');
    };

    const handleJoinMeeting = (videoLink?: string) => {
        if (videoLink) {
            window.open(videoLink, '_blank');
        }
    };

    const handleViewAll = () => {
        navigate(`${PATH.CALENDAR}`);
    };

    const handleOpenMessages = (clientId: number) => {
        window.open(`${PATH.DASHBOARD}/${PATH.INBOX}?clientId=${clientId}`, '_blank');
    };

    const formatTime = (timeStr: string) => {
        return dayjs(timeStr).tz(myTimezone?.value as string).format('h:mm A');
    };

    const formatTimeRange = (startAt: string, endAt: string) => {
        const start = dayjs(startAt).tz(myTimezone?.value as string);
        const end = dayjs(endAt).tz(myTimezone?.value as string);
        const timezone = myTimezone?.value || 'PST';
        const duration = Math.round(end.diff(start, 'minute', true));
        return `${start.format('h:mm')} - ${end.format('h:mm a')} (${timezone}) | ${duration} mins`;
    };

    const handleCopyLink = (link: string) => {
        copy(link, {
            format: 'text/plain',
        });
        message.success('Copied to clipboard.');
    };

    const getAppointmentTag = (startAt: string, endAt: string) => {
        const now = dayjs();
        const start = dayjs(startAt).tz(myTimezone?.value as string);
        const end = dayjs(endAt).tz(myTimezone?.value as string);

        // 当前时间在预约时间窗口内
        if ((now.isAfter(start) || now.isSame(start)) && (now.isBefore(end) || now.isSame(end))) {
            return {
                text: 'In session',
                bgColor: '#fee2e2',
                textColor: '#991b1b',
                iconColor: '#7f1d1d',
            };
        }

        // 预约在1小时内开始
        const minutesUntilStart = start.diff(now, 'minute');
        if (minutesUntilStart > 0 && minutesUntilStart <= 60) {
            return {
                text: 'Starting soon',
                bgColor: '#fef3c7',
                textColor: '#92400e',
                iconColor: '#78350f',
            };
        }

        // 预约在1小时之后开始
        return {
            text: 'Later today',
            bgColor: '#e0f2fe',
            textColor: '#075985',
            iconColor: '#0c4a6e',
        };
    };

    if (loading || appointments.length === 0) {
        return null;
    }

    // 如果预约数量 >= 3，只显示前3个；否则显示全部
    const displayAppointments = appointments.length >= 3 ? appointments.slice(0, 3) : appointments;
    const showViewAll = appointments.length >= 3;
    const totalCount = appointments.length;

    return (
        <div className={s.wrapper}>
            <h3 className={s.title}>
                Today&apos;s upcoming appointments ({totalCount})
            </h3>
            <div className={s.card}>
                <div className={s.content}>
                    {displayAppointments.map((appointment, index) => (
                        <React.Fragment key={appointment.id}>
                            <div className={s.appointmentItem}>
                                <div className={s.leftSection}>
                                    <div className={s.time}>{formatTime(appointment.startAt)}</div>
                                    <div className={s.details}>
                                        <div className={s.appointmentName}>
                                            {appointment.appointmentName}
                                        </div>
                                        <div className={s.patientInfo}>
                                            <div className={s.infoRow}>
                                                <div className={s.patientRow}>
                                                    {/* <div className={s.avatar}>
                                                        {appointment.patientAvatar ? (
                                                            <img
                                                                src={appointment.patientAvatar}
                                                                alt={appointment.patientName}
                                                            />
                                                        ) : (
                                                            <div className={s.avatarPlaceholder} />
                                                        )}
                                                    </div> */}
                                                    <span className={s.patientName}>
                                                        {appointment.patientName}
                                                    </span>
                                                    <div className={s.chatIcon} onClick={() => handleOpenMessages(appointment.clientId)}>
                                                        <ChatIcon />
                                                    </div>
                                                </div>
                                                <div className={s.timeInfo}>
                                                    <div className={s.clockIcon}>
                                                        <ClockIcon />
                                                    </div>
                                                    <span className={s.timeText}>
                                                        {formatTimeRange(appointment.startAt, appointment.endAt)}
                                                    </span>
                                                </div>
                                            </div>
                                            {(() => {
                                                const isVideo = appointment.contactType === EAppointmentContactTypeDictKey.VIDEO && appointment.externalVideoLink;
                                                const isInPerson = appointment.contactType === EAppointmentContactTypeDictKey.IN_PERSON && appointment.location;


                                                if (!isVideo && !isInPerson) {
                                                    return null;
                                                }

                                                // Klarity Meet 链接：显示特殊样式（带 logo 和复制按钮）
                                                if (isVideo && isKlarityMeetLink(appointment.externalVideoLink)) {
                                                    return (
                                                        <div className={s.defaultVideoLink}>
                                                            <div className={s.meetHeader}>
                                                                <div className={s.meetLogo}>
                                                                    <div className={s.logoBox} />
                                                                </div>
                                                                <p className={s.meetTitle}>
                                                                    Join with Klarity Meet
                                                                </p>
                                                            </div>
                                                            <div className={s.meetLinkWrapper}>
                                                                <a
                                                                    href={appointment.externalVideoLink}
                                                                    target="_blank"
                                                                    rel="noreferrer"
                                                                    className={s.videoLink}
                                                                >
                                                                    {appointment.externalVideoLink}
                                                                </a>
                                                                <span
                                                                    onClick={() => handleCopyLink(appointment.externalVideoLink || '')}
                                                                    className={s.copyIcon}
                                                                    title="Copy link"
                                                                >
                                                                    <svg
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                        width="24"
                                                                        height="24"
                                                                        viewBox="0 0 24 24"
                                                                        fill="none"
                                                                    >
                                                                        <path
                                                                            d="M8 4V16C8 16.5304 8.21071 17.0391 8.58579 17.4142C8.96086 17.7893 9.46957 18 10 18H18C18.5304 18 19.0391 17.7893 19.4142 17.4142C19.7893 17.0391 20 16.5304 20 16V7.242C20 6.97556 19.9467 6.71181 19.8433 6.46624C19.7399 6.22068 19.5885 5.99824 19.398 5.812L16.083 2.57C15.7094 2.20466 15.2076 2.00007 14.685 2H10C9.46957 2 8.96086 2.21071 8.58579 2.58579C8.21071 2.96086 8 3.46957 8 4Z"
                                                                            stroke="#6B7280"
                                                                            strokeWidth="2"
                                                                            strokeLinecap="round"
                                                                            strokeLinejoin="round"
                                                                        />
                                                                        <path
                                                                            d="M16 18V20C16 20.5304 15.7893 21.0391 15.4142 21.4142C15.0391 21.7893 14.5304 22 14 22H6C5.46957 22 4.96086 21.7893 4.58579 21.4142C4.21071 21.0391 4 20.5304 4 20V9C4 8.46957 4.21071 7.96086 4.58579 7.58579C4.96086 7.21071 5.46957 7 6 7H8"
                                                                            stroke="#6B7280"
                                                                            strokeWidth="2"
                                                                            strokeLinecap="round"
                                                                            strokeLinejoin="round"
                                                                        />
                                                                    </svg>
                                                                </span>
                                                            </div>
                                                        </div>
                                                    );
                                                }

                                                // 非 Klarity Meet 的视频链接：只显示简单链接（无复制按钮）
                                                if (isVideo && !isKlarityMeetLink(appointment.externalVideoLink)) {
                                                    return (
                                                        <div className={s.vItem2}>
                                                            <a
                                                                href={appointment.externalVideoLink}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                {appointment.externalVideoLink}
                                                            </a>
                                                        </div>
                                                    );
                                                }

                                                // 面对面预约：只显示位置文本（无外部链接图标）
                                                if (isInPerson) {
                                                    return (
                                                        <div className={s.linkWrapper}>
                                                            <div className={s.locationIcon}>
                                                                <svg
                                                                    xmlns="http://www.w3.org/2000/svg"
                                                                    width="24"
                                                                    height="24"
                                                                    viewBox="0 0 24 24"
                                                                    fill="none"
                                                                >
                                                                    <path
                                                                        d="M17.6569 16.6569C16.7202 17.5935 14.7616 19.5521 13.4138 20.8999C12.6327 21.681 11.3677 21.6814 10.5866 20.9003C9.26234 19.576 7.34159 17.6553 6.34315 16.6569C3.21895 13.5327 3.21895 8.46734 6.34315 5.34315C9.46734 2.21895 14.5327 2.21895 17.6569 5.34315C20.781 8.46734 20.781 13.5327 17.6569 16.6569Z"
                                                                        stroke="#6B7280"
                                                                        strokeWidth="2"
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                    />
                                                                    <path
                                                                        d="M15 11C15 12.6569 13.6569 14 12 14C10.3431 14 9 12.6569 9 11C9 9.34315 10.3431 8 12 8C13.6569 8 15 9.34315 15 11Z"
                                                                        stroke="#6B7280"
                                                                        strokeWidth="2"
                                                                        strokeLinecap="round"
                                                                        strokeLinejoin="round"
                                                                    />
                                                                </svg>
                                                            </div>
                                                            <span className={s.linkLabel}>In-person visit: </span>
                                                            <a
                                                                href={`https://maps.google.com/?q=${encodeURIComponent(
                                                                    appointment.location || '',
                                                                )}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className={s.link}
                                                            >
                                                                {appointment.location}2312312
                                                                <div className={s.externalLinkIcon}>
                                                                    <ExternalLinkIcon />
                                                                </div>
                                                            </a>
                                                        </div>
                                                    );
                                                }

                                                return null;
                                            })()}
                                        </div>
                                    </div>
                                </div>
                                <div className={s.rightSection}>
                                    {(() => {
                                        const tag = getAppointmentTag(appointment.startAt, appointment.endAt);
                                        return (
                                            <>
                                                <div
                                                    className={s.appointmentTag}
                                                    style={{
                                                        backgroundColor: tag.bgColor,
                                                        color: tag.textColor,
                                                    }}
                                                >
                                                    <ClockIconColored color={tag.iconColor} />
                                                    <span className={s.tagText}>{tag.text}</span>
                                                </div>
                                                {/* 如果是 "In session" 状态且有视频链接，显示 "Join now" 按钮 */}
                                                {tag.text === 'In session' && appointment.externalVideoLink ? (
                                                    <Button
                                                        type='primary'
                                                        className={s.joinButton}
                                                        onClick={() => handleJoinMeeting(appointment.externalVideoLink)}
                                                    >
                                                        Join now
                                                    </Button>
                                                ) : (
                                                    <Button
                                                        className={s.viewButton}
                                                        onClick={() => handleViewProfile(appointment.clientId)}
                                                    >
                                                        View profile
                                                    </Button>
                                                )}
                                            </>
                                        );
                                    })()}
                                </div>
                            </div>
                            {index < displayAppointments.length - 1 && <div className={s.divider} />}
                        </React.Fragment>
                    ))}
                    {showViewAll && (
                        <div className={s.viewAll} onClick={handleViewAll}>
                            <span className={s.viewAllText}>View all</span>
                            <div className={s.arrowIcon}>
                                <ArrowRightIcon />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TodayAppointments;
